import { ColumnDef } from "@tanstack/react-table";
import { DataTable } from "@common/components/Table/DataTable";
import { DataTableColumnHeader } from "@common/components/Table/DataTableColumnHeader";
import { IconBox } from "@common/components";
import { Body } from "@hypercard/web-components";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/components/ui/use-toast";
import { getDepartmentDisplay } from "../../util";
import { entireCompany } from "@common/components/SelectDepartment";
import { formatDollars } from "@common/components/utils";
import { StipendRule } from "./types";
import {
  StipendRulesDocument,
  useUpdateStipendRuleMutation,
} from "@common/utils/graphql";
import { useApolloClient } from "@apollo/client";

const ToggleStipendStatusCell = ({
  row: { original },
}: {
  row: { original: StipendRule };
}) => {
  const client = useApolloClient();
  const { toast } = useToast();
  const [updateStipendRule, { loading }] = useUpdateStipendRuleMutation({
    refetchQueries: [StipendRulesDocument],
  });

  const toggleStatus = async ({
    variables,
  }: {
    variables: { input: { isEnabled: boolean }; stipendRuleId: number };
  }) => {
    const { data } = await updateStipendRule({ variables });
    return data;
  };

  const handleToggle = async (checked: boolean) => {
    try {
      // Optimistically update the cache
      client.cache.modify({
        id: client.cache.identify(original),
        fields: {
          isEnabled() {
            return checked;
          },
        },
      });

      const data = await toggleStatus({
        variables: {
          input: {
            isEnabled: checked,
          },
          stipendRuleId: original.id,
        },
      });

      if (
        data?.updateStipendRule.__typename !==
        "MutationUpdateStipendRuleSuccess"
      ) {
        throw new Error();
      }
      toast({
        title: `Stipend status successfully ${checked ? "activated" : "deactivated"}`,
      });
    } catch (e) {
      toast({
        title: `Failed to update stipend status, please try again`,
        variant: "destructive",
      });
    }
  };

  return (
    <Switch
      checked={original.isEnabled}
      onCheckedChange={handleToggle}
      disabled={loading}
    />
  );
};

const getStipendRuleColumns = (
  onEdit: (stipendRule: StipendRule) => void,
): ColumnDef<StipendRule>[] => [
  {
    accessorKey: "name",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Stipend Name" />
    ),
    cell: ({ row }) => (
      <div className="flex items-center gap-x-4">
        <IconBox className="shrink-0">
          <img
            className="size-6"
            src={row.original.iconUrl}
            alt={row.original.name}
          />
        </IconBox>
        <div>
          <Body size="md" weight="regular">
            {row.original.name}
          </Body>
          <Body size="sm" weight="regular" className="text-muted-foreground">
            {row.original.numberOfClaims} Claimed
          </Body>
        </div>
      </div>
    ),
  },
  {
    accessorKey: "amount",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Amount Claimed" />
    ),
    cell: ({ row }) => (
      <Body size="md" weight="regular">
        {formatDollars(row.original.amountClaimed)}
      </Body>
    ),
  },
  {
    accessorKey: "department",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Department" />
    ),
    cell: ({ row }) => {
      const departments =
        row.original.departments && row.original.departments.length > 0
          ? row.original.departments
          : [entireCompany];
      return (
        <Body size="md" weight="regular">
          {getDepartmentDisplay(departments)}
        </Body>
      );
    },
  },
  {
    accessorKey: "isActive",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) =>
      row.original.expiresAt &&
      new Date(row.original.expiresAt) < new Date() ? (
        <Body size="md" weight="medium">
          Expired
        </Body>
      ) : (
        <ToggleStipendStatusCell row={row} />
      ),
  },
  {
    id: "actions",
    cell: ({ row }) => (
      <Button variant="secondary" onClick={() => onEdit(row.original)}>
        Edit Stipend
      </Button>
    ),
  },
];

type Props = {
  stipendRules: StipendRule[];
  onEdit: (stipendRule: StipendRule) => void;
};
export const StipendTable = ({ stipendRules, onEdit }: Props) => {
  const columns = getStipendRuleColumns(onEdit);
  return <DataTable columns={columns} data={stipendRules} />;
};
